import React, { useEffect, useState, useRef } from "react";
import PropTypes from "prop-types";
import { Box, MenuItem, FormControl, Checkbox, ListSubheader } from "@mui/material";
import KeyboardArrowDownIcon from "@mui/icons-material/KeyboardArrowDown";
import KeyboardArrowUpIcon from "@mui/icons-material/KeyboardArrowUp";
import ExpandMore from "@mui/icons-material/ExpandMore";
import StyledSelect from "../StyledSelect/StyledSelect";
import StyledInputLabel from "../StyledInputLabel/StyledInputLabel";
import StyledFormHelperText from "../StyledFormHelperText/StyledFormHelperText";

export default function PrimarySelect({
  id,
  label,
  customLabel,
  options,
  defaultOption,
  onChangeOptions,
  onChange,
  flexGrow,
  width,
  value,
  variant,
  nameKey,
  valueKey,
  parentValueKey,
  parentNameKey,
  childrenKey,
  disabled,
  multiple,
  error,
  optionsHeight,
  helperText,
  allOption
}) {
  const [allCheckbox, setAllCheckbox] = useState("");
  const menuListRef = useRef(null);

  const handleDropdownOpen = () => {
    setTimeout(() => {      
      if (menuListRef.current) {
        menuListRef.current.parentElement.scrollTop = 0;
      }
    }, 0);
  };

  const handleChange = event => {
    if (multiple && allOption) {
      if (event.target.value.includes("None")) {
        onChange([]);
      } else if (event.target.value.includes("All") && parentValueKey) {
        onChange(options.map(option => option[childrenKey].map(child => child[valueKey])).flat());
      } else if (event.target.value.includes("All")) {
        onChange(valueKey ? options.map(option => option[valueKey]) : options);
      } else {
        onChange(event.target.value);
      }
    } else {
      onChange(event.target.value);
    }
  };

  const handleChangeAll = option => {
    let updatedValue = [...value];
    options.map(row => {
      if (row[parentValueKey] === option) {
        if (row.children.every(child => updatedValue.includes(child[valueKey]))) {
          row.children.forEach(child => {
            if (updatedValue.includes(child[valueKey])) {
              updatedValue.splice(updatedValue.indexOf(child[valueKey]), 1);
            }
          });
        } else if (row.children.every(child => !updatedValue.includes(child[valueKey])) || (row.children.some(child => updatedValue.includes(child[valueKey])) &&
          row.children.some(child => !updatedValue.includes(child[valueKey])))) {
          updatedValue = Array.from(new Set(value.concat(row.children.map(child => { return child[valueKey]; }))));
        }
      }
      return row;
    });
    onChange(updatedValue);
  };

  const handleOpen = option => {
    onChangeOptions(options =>
      options.map(row => {
        if (row[parentValueKey] === option) {
          return {
            ...row,
            open: !row.open,
          };
        }
        return row;
      })
    );
  };

  const renderValue = () => {
    if (multiple && nameKey) {
      let valuesNames = [];
      value.forEach(v => {
        valuesNames.push(options.find(option => option[valueKey] == v)?.[nameKey]);
      });
      return valuesNames.join(", ");
    } else if (multiple && !nameKey) {
      return value.join(", ");
    } else if (!multiple && nameKey) {
      return options.find(option => option[valueKey] == value)?.[nameKey] || defaultOption;
    } else {
      return options.find(option => option == value) || defaultOption;
    }
  };

  useEffect(() => {
    if (parentValueKey && value.length === options.map(option => option[childrenKey].map(child => child[valueKey])).flat().length) {
      setAllCheckbox("None");
    } else if (value.length === options.length) {
      setAllCheckbox("None");
    } else {
      setAllCheckbox("All");
    }
  }, [value]);

  return (
    <>
      <FormControl variant={variant} sx={{ flexGrow: flexGrow || 0 }} error={error} disabled={disabled}>
        {label && <StyledInputLabel>{label}</StyledInputLabel>}
        {!parentValueKey &&
          <StyledSelect
            labelId="primary-select-label"
            id={id}
            label={label}
            value={value}
            multiple={multiple}
            renderValue={renderValue}
            variant={variant}
            displayEmpty
            size="small"
            IconComponent={ExpandMore}
            sx={{
              width: width,
            }}
            onChange={handleChange}
            disabled={disabled}
            onOpen={handleDropdownOpen}
            MenuProps={{
              MenuListProps: {
                ref: menuListRef
              },
              PaperProps: { sx: { maxHeight: optionsHeight || "80vh", boxShadow: "elevation1" } }
            }}
            {...(variant === "standard" && { disableUnderline: true })}
          >
            {defaultOption && <MenuItem value="">{defaultOption}</MenuItem>}
            {multiple && allOption &&
              <MenuItem value={allCheckbox} sx={{ "&.Mui-selected": { borderColor: "primary.60", borderWidth: "2px", borderStyle: "none none none solid" } }}>
                <Checkbox
                  checked={value.length === options.length}
                  sx={{
                    color: "black_solid.40",
                    "&.Mui-checked": {
                      color: "primary.60"
                    }
                  }}
                />
                All
              </MenuItem>
            }
            {nameKey ? (
              options.map((option) => (
                <MenuItem key={option[valueKey]} value={option[valueKey]} sx={{ "&.Mui-selected": { borderColor: "primary.60", borderWidth: "2px", borderStyle: "none none none solid" } }}>
                  {multiple &&
                    <Checkbox
                      checked={value.includes(option[valueKey])}
                      sx={{
                        color: "black_solid.40",
                        "&.Mui-checked": {
                          color: "primary.60"
                        }
                      }}
                    />
                  }
                  {option[nameKey]}
                </MenuItem>
              ))
            ) : (
              options.map((option) => (
                <MenuItem key={option} value={option} sx={{ "&.Mui-selected": { borderColor: "primary.60", borderWidth: "2px", borderStyle: "none none none solid" } }}>
                  {multiple &&
                    <Checkbox
                      checked={value.includes(option)}
                      sx={{
                        color: "black_solid.40",
                        "&.Mui-checked": {
                          color: "primary.60"
                        }
                      }}
                    />
                  }
                  {option}
                </MenuItem>
              ))
            )}
          </StyledSelect>
        }
        {parentValueKey &&
          <StyledSelect
            labelId="primary-group-select-label"
            id={id}
            label={label}
            value={value}
            multiple
            renderValue={(value) => customLabel || `${value?.length} selected`}
            displayEmpty
            variant={variant}
            size="small"
            IconComponent={ExpandMore}
            sx={{
              width: width,
            }}
            onChange={handleChange}
            disabled={disabled}
            onOpen={handleDropdownOpen}
            MenuProps={{
              MenuListProps: {
                ref: menuListRef
              },
              PaperProps: { sx: { maxHeight: optionsHeight || "80vh", boxShadow: "elevation1" } }
            }}
            {...(variant === "standard" && { disableUnderline: true })}
          >
            {allOption &&
              <MenuItem value={allCheckbox} sx={{ "&.Mui-selected": { borderColor: "primary.60", borderWidth: "2px", borderStyle: "none none none solid" } }}>
                <Checkbox
                  checked={value.length === options.map(option => option[childrenKey].map(child => child[valueKey])).flat().length}
                  sx={{
                    color: "black_solid.40",
                    "&.Mui-checked": {
                      color: "primary.60"
                    }
                  }}
                />
                All
              </MenuItem>
            }
            {options.reduce((items, option) => [
              ...items,
              <ListSubheader key={option[parentValueKey]}>
                <Box sx={{ display: "flex", alignItems: "center" }}>
                  {option.open
                    ? <KeyboardArrowUpIcon sx={{ fontSize: "20px", color: "black_opacity.70", cursor: "pointer" }} onClick={() => handleOpen(option[parentValueKey])} />
                    : <KeyboardArrowDownIcon sx={{ fontSize: "20px", color: "black_opacity.70", cursor: "pointer" }} onClick={() => handleOpen(option[parentValueKey])} />
                  }
                  <Checkbox
                    onChange={() => handleChangeAll(option[parentValueKey])}
                    checked={option[childrenKey].every(child => value.includes(child[valueKey]))}
                    indeterminate={option[childrenKey].some(child => value.includes(child[valueKey])) && option[childrenKey].some(child => !value.includes(child[valueKey]))}
                    sx={{
                      color: "black_solid.40",
                      "&.Mui-checked": {
                        color: "primary.60"
                      },
                      cursor: "default"
                    }}
                  />
                  {option[parentNameKey]}
                </Box>
              </ListSubheader>,
              ...option[childrenKey].map((child) => (
                <MenuItem
                  key={child[valueKey]}
                  value={child[valueKey]}
                  sx={{
                    display: option.open ? "flex" : "none",
                    paddingLeft: "70px",
                    "&.Mui-selected": { borderColor: "primary.60", borderWidth: "2px", borderStyle: "none none none solid" }
                  }}>
                  <Checkbox
                    checked={value.includes(child[valueKey])}
                    sx={{
                      color: "black_solid.40",
                      "&.Mui-checked": {
                        color: "primary.60"
                      }
                    }}
                  />
                  {child[nameKey]}
                </MenuItem>
              ))
            ], [])}
          </StyledSelect>
        }
      </FormControl>
      <StyledFormHelperText error={error} disabled={disabled}>{helperText}</StyledFormHelperText>
    </>
  );
}

PrimarySelect.propTypes = {
  id: PropTypes.string,
  label: PropTypes.string,
  customLabel: PropTypes.string,
  options: PropTypes.array.isRequired,
  defaultOption: PropTypes.string,
  onChangeOptions: PropTypes.func,
  onChange: PropTypes.func.isRequired,
  flexGrow: PropTypes.number,
  width: PropTypes.string,
  value: PropTypes.any.isRequired,
  variant: PropTypes.string,
  nameKey: PropTypes.string,
  valueKey: PropTypes.string,
  parentValueKey: PropTypes.string,
  parentNameKey: PropTypes.string,
  childrenKey: PropTypes.string,
  disabled: PropTypes.bool,
  multiple: PropTypes.bool,
  error: PropTypes.bool,
  optionsHeight: PropTypes.string,
  helperText: PropTypes.string,
  allOption: PropTypes.bool
};